import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import { useAuth } from '@/hooks/use-auth';
import { usePlatformIntegrations } from '@/hooks/usePlatformIntegrations';
import { assignPostToSlot, getPlanVersion } from '@/lib/plan-slots/assign-slot';
import { POSTABLE_PLATFORMS } from '@/lib/platforms/registry';
import { isPristineManualDraft, watchAbandonedDraft } from '@/lib/posts/abandoned-draft';
import { normalizeScheduledAt } from '@/lib/posts/scheduled-at';
import type { QuickPostInput } from '@/services/data/QuickPostService';
import { quickPostService } from '@/services/data/QuickPostService';
import { logger } from '@/utils/logger';
import { showErrorToast, showSuccessToast } from '@/utils/toast';

export interface ManualPostDraftInput {
  content?: string;
  mediaUrls?: string[];
  platforms?: string[];
  scheduledAt?: string | Date | null;
  title?: string | null;
  mediaType?: string | null;
  sourceUrl?: string | null;
  sourceArticleId?: string | null;
  planId?: string | null;
  slotId?: string | null;
  openInspector?: boolean;
  showSuccessToast?: boolean;
}

export interface ManualPostDraftResult {
  postId: string;
  slotAssigned: boolean;
}

async function invalidateManualDraftQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId?: string,
  postId?: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['posts'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['calendar-posts'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['posts_infinite'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['calendar-tab-posts'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['calendar-tab-count'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['draft-posts-count'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['slot-posts'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['planSlots'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['unbound-plan-posts'], exact: false }),
    queryClient.invalidateQueries({ queryKey: ['library'], exact: false }),
    postId
      ? queryClient.invalidateQueries({ queryKey: ['post', postId], exact: false })
      : Promise.resolve(),
    userId
      ? queryClient.resetQueries({ queryKey: ['posts', userId], exact: false })
      : Promise.resolve(),
  ]);
}

export function useManualPostDraft() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const inspector = useInspector();
  const { connectedAccounts } = usePlatformIntegrations();

  const mutation = useMutation({
    mutationFn: async (input: ManualPostDraftInput): Promise<ManualPostDraftResult> => {
      // A caller that omits platforms entirely (the sidebar's bare "New
      // post" button) used to fall all the way through to
      // buildQuickPostInsert's own ['instagram'] fallback regardless of
      // whether Instagram was even connected — reported live, 2026-09-16:
      // every fresh draft opened pre-attached to Instagram, forcing every
      // other connected platform (LinkedIn) behind an extra click, even
      // when Instagram had never been connected at all. Default to the
      // first CONNECTED platform instead; only fall back to that deep
      // ['instagram'] default when the workspace has no connections yet.
      const firstPostable = connectedAccounts.find((account) =>
        (POSTABLE_PLATFORMS as string[]).includes(account.platform),
      )?.platform;
      const platforms = input.platforms ?? (firstPostable ? [firstPostable] : undefined);
      const quickInput: QuickPostInput = {
        type: 'scratch',
        content: input.content ?? '',
        mediaUrls: input.mediaUrls ?? [],
        platforms,
        scheduledAt: normalizeScheduledAt(input.scheduledAt),
        title: input.title,
        mediaType: input.mediaType,
        sourceUrl: input.sourceUrl,
        sourceArticleId: input.sourceArticleId,
        campaignId: input.planId,
      };

      const response = await quickPostService.createQuickDraft(quickInput);
      if (response.error || !response.data) {
        throw new Error(response.error?.message ?? 'Could not create the post.');
      }

      const postId = response.data.id;
      let slotAssigned = false;

      if (input.planId && input.slotId) {
        try {
          const expectedVersion = await getPlanVersion(input.planId);
          const bind = await assignPostToSlot(input.planId, input.slotId, postId, expectedVersion);
          slotAssigned = bind.success;

          if (bind.conflict) {
            toast.message('Draft created. The project slot changed, so assign it from the editor.');
          } else if (!bind.success) {
            toast.message('Draft created. Assign it to the slot from the editor.');
          }
        } catch (err) {
          logger.error('Failed to assign manual draft to slot', err);
          toast.message('Draft created. Assign it to the slot from the editor.');
        }
      }

      await invalidateManualDraftQueries(queryClient, user?.id, postId);
      if (response.data.post) {
        queryClient.setQueryData(['post', postId, user?.id], response.data.post);
      }

      if (input.openInspector ?? true) {
        inspector.openSubject(
          { kind: 'post', postId },
          { presentation: 'workspace', backToProjectId: input.planId ?? null },
        );
        if (!input.content && !input.mediaUrls?.length && !input.planId && !slotAssigned) {
          watchAbandonedDraft(postId, async (abandonedId) => {
            try {
              const { postService } = await import('@/services/data/PostService');
              const current = await postService.getPost(abandonedId);
              if (!isPristineManualDraft(current.data)) return;
              const removed = await postService.deletePost(abandonedId);
              if (removed.error) return;
              await invalidateManualDraftQueries(queryClient, user?.id, abandonedId);
            } catch (error) {
              logger.error('Failed to discard an untouched draft', error);
            }
          });
        }
      }

      if (input.showSuccessToast) {
        showSuccessToast('Draft created');
      }

      return { postId, slotAssigned };
    },
    onError: (error: Error) => {
      showErrorToast('Could not start a post', error.message);
    },
  });

  const createManualPostDraft = useCallback(
    (input: ManualPostDraftInput = {}) => mutation.mutateAsync(input),
    [mutation],
  );

  return {
    createManualPostDraft,
    isCreatingManualPost: mutation.isPending,
  };
}
